import React, { memo, useRef, useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { dates, formats, validators, arrays } from 'investira.sdk';
import {
  Stack,
  Card,
  Icon,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  DatePicker,
  Divider,
  Typography
} from 'investira.react.components';
import { StocksChart } from 'investira.react.charts';
import { COTACAO_TIPO } from '@investirapri/financa.js/lib/enums';
import { VALIDATIONS } from '../../const';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const IndicadoresTabular = memo(props => {
  const { dataInicio, dataFim, indicador, cotacoes, isPageLoading } = props;
  const xCotacaoTipo = arrays.arrayToObject(Object.values(COTACAO_TIPO), 'id');
  const { indicador_id, divulgacao } = indicador;

  const VALIDATION_SCHEMA = Yup.object().shape({
    data_inicio: Yup.date().max(new Date()).required(VALIDATIONS.REQUIRED),
    data_fim: Yup.date().max(new Date()).required(VALIDATIONS.REQUIRED)
  });

  const formik = useFormik({
    initialValues: { data_inicio: dayjs().add(-6, 'month'), data_fim: dayjs() },
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: false,
    onSubmit: (pValues, pActions) => {
      pActions.setSubmitting(true);
    }
  });

  const handleChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  const filtrarPorData = (pDados, pDataInicio, pDataFim) => {
    return pDados.filter(
      xItem =>
        dayjs(xItem.data).isSameOrAfter(pDataInicio, 'day') &&
        dayjs(xItem.data).isSameOrBefore(pDataFim, 'day')
    );
  };

  if (validators.isEmpty(cotacoes[0].data)) {
    return null;
  }

  const xCotacoesFiltered = filtrarPorData(
    cotacoes[0].data,
    formik.values.data_inicio,
    formik.values.data_fim
  );

  const calcularVariaPercentual = pCotacoes => {
    if (!Array.isArray(pCotacoes) || pCotacoes.length < 2) {
      return null;
    }
    const xPrimeira = pCotacoes[0];
    const xUltima = pCotacoes[pCotacoes.length - 1];

    if (
      validators.isNull(xPrimeira?.cotacao) ||
      validators.isNull(xUltima?.cotacao) ||
      Number(xPrimeira.cotacao) === 0
    ) {
      return null;
    }

    const xVariaPercentual =
      ((Number(xUltima.cotacao) - Number(xPrimeira.cotacao)) / Number(xPrimeira.cotacao)) * 100;

    return xVariaPercentual;
  };

  const xVariaPercentual = calcularVariaPercentual(xCotacoesFiltered);

  return (
    <Stack>
      <Stack>
        <TableContainer component={Paper}>
          <Stack sx={{ gap: '16px 16px', px: 2, py: 2 }} flexWrap="wrap" direction="row">
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Variação no período
              </Typography>
              <Typography variant="h6" color="textPrimary">
                {xVariaPercentual ? formats.formatNumber(xVariaPercentual) : '---'}%
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ gap: 2 }}>
              <DatePicker
                label="Data Início"
                name="data"
                onChange={pValue => {
                  handleChange('data_inicio', pValue);
                }}
                value={formik.values.data_inicio}
                error={Boolean(formik.errors.data_inicio)}
                helperText={formik.errors.data_inicio}
                disabled={formik.isSubmitting}
                minDate={dayjs(dataInicio)}
                fullWidth
              />
              <DatePicker
                label="Data Fim"
                name="data"
                onChange={pValue => {
                  handleChange('data', pValue);
                }}
                value={formik.values.data_fim}
                error={Boolean(formik.errors.data_fim)}
                helperText={formik.errors.data_fim}
                disabled={formik.isSubmitting}
                maxDate={dayjs(dataFim)}
                fullWidth
              />
            </Stack>
          </Stack>
          <Divider />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                {/* <TableCell>Data Divulgação</TableCell> */}
                <TableCell align="right">Cotação (R$)</TableCell>
                <TableCell align="right">Taxa (%)</TableCell>
                <TableCell>Cotação Tipo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {xCotacoesFiltered.reverse().map(row => (
                <TableRow
                  key={crypto.randomUUID()}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell>
                    {divulgacao.periodicidade === 'M'
                      ? formats.formatDateCustom(row.data, 'MMM/YY')
                      : formats.formatDate(row.data)}
                  </TableCell>
                  {/* <TableCell>
                    {formats.formatDate(row.data_divulgacao)}
                  </TableCell> */}
                  <TableCell align="right">
                    {row.cotacao && formats.formatNumber(row.cotacao, 8)}
                  </TableCell>
                  <TableCell align="right">{row.taxa}</TableCell>
                  <TableCell>{xCotacaoTipo[row.cotacao_tipo_id].descricao}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Stack>
  );
});

IndicadoresTabular.displayName = 'IndicadoresTabular';

export default IndicadoresTabular;
